
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, GraduationCap, Upload, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import BulkImportTeachers from "../components/admin/BulkImportTeachers";

export default function Teachers() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const queryClient = useQueryClient();

  const { data: allUsers = [], isLoading } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => base44.entities.User.list(),
  });

  // Filter only teachers
  const teachers = allUsers.filter(user => user.role === 'user' && user.user_type === 'teacher');

  const filteredTeachers = teachers.filter(teacher =>
    teacher.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.teacher_full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDisplayName = (teacher) => {
    return teacher.teacher_full_name || teacher.full_name || teacher.email;
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Teachers</h1>
              <p className="text-slate-600 mt-1">Manage Teacher Accounts and Permissions</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowBulkImport(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Update Teacher Names
          </Button>
        </div>

        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            <strong>📝 Update Teacher Names:</strong> Click "Update Teacher Names" to bulk import real names from your teacher list. Teachers will display as their full names (e.g., "CHANTELLE NEWSOME") instead of email usernames (e.g., "cnewsome").
          </AlertDescription>
        </Alert>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Grade Level</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : filteredTeachers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        No teachers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTeachers.map((teacher) => (
                      <TableRow key={teacher.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {getDisplayName(teacher)}
                          {!teacher.teacher_full_name && (
                            <Badge className="ml-2 bg-orange-100 text-orange-800 text-xs">
                              Name not set
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{teacher.email}</TableCell>
                        <TableCell>
                          {teacher.teacher_grade_level ? (
                            <Badge className="bg-blue-100 text-blue-800">
                              Grade {teacher.teacher_grade_level}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">Not set</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">
                            Teacher
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg bg-blue-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Teacher Permissions:</h3>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>View their own roster of students with failing grades</li>
              <li>Review and approve/deny excusal requests from students</li>
              <li>Manually assign students to EA</li>
              <li>Cannot access full rosters, reports, or student database</li>
              <li>Cannot manage other users or system settings</li>
            </ul>
          </CardContent>
        </Card>

        {showBulkImport && (
          <BulkImportTeachers 
            onClose={() => setShowBulkImport(false)}
            onSuccess={() => {
              setShowBulkImport(false);
              queryClient.invalidateQueries(['allUsers']);
            }}
          />
        )}
      </div>
    </div>
  );
}
